import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import RFQStatusBadge from './RFQStatusBadge';

const RFQTable = ({ rfqs, onViewDetails, onDownloadQuote, onAcceptQuote, onRequestModification }) => {
  const [sortField, setSortField] = useState('submissionDate');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedRFQs = [...rfqs]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];

    if (sortField === 'submissionDate' || sortField === 'estimatedResponse') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getEstimatedResponseText = (date, status) => {
    if (status === 'quoted' || status === 'accepted' || status === 'declined') {
      return 'Completed';
    }
    
    const now = new Date();
    const estimatedDate = new Date(date);
    const diffTime = estimatedDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'Overdue';
    } else if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Tomorrow';
    } else {
      return `${diffDays} days`;
    }
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-smooth"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        <Icon 
          name={sortField === field ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
          size={14} 
        />
      </div>
    </th>
  );

  if (rfqs?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No RFQs Found</h3>
        <p className="text-muted-foreground mb-4">You haven't submitted any requests for quotation yet.</p>
        <Link to="/rfq-request-form">
          <Button variant="default" iconName="Plus" iconPosition="left">
            Create New RFQ
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <SortableHeader field="rfqNumber">RFQ Number</SortableHeader>
              <SortableHeader field="serviceCategory">Service Category</SortableHeader>
              <SortableHeader field="submissionDate">Submission Date</SortableHeader>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <SortableHeader field="salesRep">Sales Rep</SortableHeader>
              <SortableHeader field="estimatedResponse">Response Time</SortableHeader>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {sortedRFQs?.map((rfq) => (
              <tr key={rfq?.id} className="hover:bg-muted/30 transition-smooth">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Icon name="FileText" size={16} className="text-muted-foreground mr-2" />
                    <span className="text-sm font-medium text-foreground">{rfq?.rfqNumber}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-foreground">{rfq?.serviceCategory}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-foreground">{formatDate(rfq?.submissionDate)}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <RFQStatusBadge status={rfq?.status} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mr-2">
                      <span className="text-xs font-medium text-primary-foreground">
                        {rfq?.salesRep?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <span className="text-sm text-foreground">{rfq?.salesRep}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`text-sm ${
                    getEstimatedResponseText(rfq?.estimatedResponse, rfq?.status) === 'Overdue' ?'text-red-600 font-medium' :'text-foreground'
                  }`}>
                    {getEstimatedResponseText(rfq?.estimatedResponse, rfq?.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(rfq)}
                      iconName="Eye"
                    >
                      View
                    </Button>
                    
                    {rfq?.status === 'quoted' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onDownloadQuote(rfq)}
                          iconName="Download"
                        >
                          Download
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => onAcceptQuote(rfq)}
                          iconName="Check"
                        >
                          Accept
                        </Button>
                      </>
                    )}
                    
                    {(rfq?.status === 'quoted' || rfq?.status === 'under_review') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRequestModification(rfq)}
                        iconName="Edit"
                      >
                        Modify
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RFQTable;